<template>
  <div>
    <h2 v-if="compareSurveySession">
      {{ $t('survey.comparison') }} {{ companyName }} <small>({{ surveyDateString }})</small> & {{ comparisonCompanyName }} <small>({{ comparisonSurveyDateString }})</small>
    </h2>
    <h2 v-else>
      {{ compareSectorTitle }}{{ $t('surveyResult.referenceYear', { year: this.reference.Year }) }}
    </h2>
    <p class="padded" v-intro="$t('surveyResult.introjsTailleEchantillon')" v-intro-step="7">
      {{ $t('surveyResult.referenceCountIntro') }} <strong>{{ $t('surveyResult.referenceCount', { count: this.countReference} ) }}</strong> {{ referenceCount }}
      {{ $t('surveyResult.referenceMean') }} <strong>{{ meanReference }}</strong>.
    </p>
    <div class="charts">
      <div>
        <div class="chart-container" v-intro="$t('surveyResult.introjsHistogramme')" v-intro-step="4">
          <GlobalDistributionChartComponent
            :chart-data="globalDistributionData"
            :options="globalDistributionOption"
            :myPositionIndex="myPositionIndex"
            :comparisonPositionIndex="comparisonPositionIndex"
            v-on:myAveragePosX="averagePosXUpdated"
            v-on:comparisonAveragePosX="comparisonAveragePosXUpdated">
          </GlobalDistributionChartComponent>
          <div
            class="my-average"
            v-bind:style="{ left: myAverageArrowPosX + 'px' }"
            v-intro="$t('surveyResult.introjsScore')" v-intro-step="3">
            <div class="arrow-up"></div>
            {{ $t('surveyResult.averageOf') }}<br />
            <div class="company-name">{{ companyName }}</div>
            <br /><span class="my-score">{{ getMyScore() }}</span>
            <div class="extension-line"></div>
          </div>
          <div
            v-if="compareSurveySession"
            class="my-average comparison"
            v-bind:style="{ left: comparisonAverageArrowPosX + 'px' }">
            <div class="arrow-up"></div>
            {{ $t('surveyResult.averageOf') }}<br />
            <div class="company-name">{{ comparisonCompanyName }}</div>
            <br /><span class="my-score">{{ getComparisonScore() }}</span>
            <div class="extension-line"></div>
          </div>
        </div>
      </div>
      <div>
        <div class="chart-container" v-intro="$t('surveyResult.introjsDiagramme')" v-intro-step="5">
          <SectorComparisonChartComponent
            :chart-data="sectorComparisonData"
            :options="sectorComparisonOption">
          </SectorComparisonChartComponent>
        </div>
        <router-link to="/about-axis" target="_blank" class="subtitle print-hidden" v-if="this.reference.Year === 2020">
          <span class="c-tooltip">
            <span class="c-tooltip__icon tt-info">i</span>
          </span>
          {{ $t('surveyResult.aboutAxis') }}
        </router-link>
        <div class="dataset-table-container" v-if="sectorComparisonData && sectorComparisonData.datasets.length > 1">
          <table>
            <tr>
              <th></th>
              <th :style="`width: 150px; padding: 1em 0; font-size: 10px; border-bottom: 2px solid ${sectorComparisonData.datasets[0].borderColor}`">
                {{ $t('surveyResult.average') }}
              </th>
              <th :style="`width: 150px; padding: 1em 0; font-size: 10px; border-bottom: 2px solid ${sectorComparisonData.datasets[1].borderColor}`">
                <template v-if="compareSurveySession">
                  {{ companyName }}
                  <br />{{ surveyDateString }}
                </template>
                <template v-else>
                  {{ $t('surveyResult.yourEnterprise') }}
                </template>
              </th>
              <th :style="`width: 150px; padding: 1em 0; font-size: 10px; border-bottom: 2px solid ${sectorComparisonData.datasets[2].borderColor}`" v-if="compareSurveySession">
                {{ comparisonCompanyName }}
                <br />{{ comparisonSurveyDateString }}
              </th>
            </tr>
            <tr><td colspan="3"><br /></td></tr>
            <tr v-for="(label, i) of sectorComparisonData.labels" :key="label">
              <td>{{ label }}</td>
              <td style="text-align: center;">
                {{ sectorComparisonData.datasets[0].data[i] }}
              </td>
              <td style="text-align: center;">
                {{ sectorComparisonData.datasets[1].data[i] }}
              </td>
              <td v-if="compareSurveySession" style="text-align: center;">
                {{ sectorComparisonData.datasets[2].data[i] }}
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectorComparisonChartComponent from '@/components/SectorComparisonChart.vue';
import GlobalDistributionChartComponent from '@/components/GlobalDistributionChart.vue';

export default {
  name: 'surveyResult',
  components: {
    SectorComparisonChartComponent,
    GlobalDistributionChartComponent,
  },
  data() {
    return {
      sectorComparisonData: {},
      globalDistributionData: {},
      globalDistributionOption: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: this.$t('surveyResult.globalDistributionXAxeLabel'),
            },
          }],
          yAxes: [{
            scaleLabel: {
              display: true,
              labelString: this.$t('surveyResult.globalDistributionYAxeLabel'),
            },
            ticks: {
              callback: (value) => `${value}%`,
            },
          }],
        },
      },
      sectorComparisonOption: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          align: 'start',
        },
        scale: {
          ticks: {
            suggestedMin: 0,
            stepSize: 10,
          },
          pointLabels: {
            fontSize: 14,
          },
        },
        showAllTooltips: true,
      },
      myAverageArrowPosX: 0,
      comparisonAverageArrowPosX: 0,
      myPositionIndex: 0,
      comparisonPositionIndex: 0,
      myColor: 'rgba(238, 114, 3, 0.6)',
      otherColor: 'rgba(11, 146, 210, 0.6)',
      otherColorFaded: 'rgba(11, 146, 210, 0.2)',
      compareColor: 'rgba(176, 49, 100, 0.6)',
      countReference: Number,
      meanReference: Number,
    };
  },
  props: {
    companyName: String,
    surveyDateString: String,
    comparisonCompanyName: String,
    comparisonSurveyDateString: String,
    reference: Object,
    code: String,
    category: String,
    surveySession: Object,
    compareSurveySession: Object,
  },
  watch: {
    surveySession: {
      handler(val) {
        if (val !== undefined) {
          this.fillData();
        }
      },
      deep: true,
      immediate: true,
    },
    reference: {
      handler(val) {
        if (val !== undefined) {
          this.fillData();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    averagePosXUpdated(value) {
      this.myAverageArrowPosX = value;
    },
    comparisonAveragePosXUpdated(value) {
      this.comparisonAverageArrowPosX = value;
    },
    getMyScore() {
      return Math.round((this.surveySession.Infrastructure.Value
              + this.surveySession.HumanResources.Value
              + this.surveySession.ProcessFlow.Value
              + this.surveySession.SalesProducts.Value) / 4);
    },
    getComparisonScore() {
      return Math.round((this.compareSurveySession.Infrastructure.Value
              + this.compareSurveySession.HumanResources.Value
              + this.compareSurveySession.ProcessFlow.Value
              + this.compareSurveySession.SalesProducts.Value) / 4);
    },
    async fillData() {
      this.countReference = this.reference.Sample.Actual;
      this.meanReference = this.reference.Global.Mean;
      // Sector Comparison
      this.sectorComparisonData = {
        labels: [
          this.reference.Year === 2020 ? this.$t('surveyResult.infrastructure') : this.$t('surveyResult.howToRead2022.section1.axis1-title'),
          this.reference.Year === 2020 ? this.$t('surveyResult.humanResources') : this.$t('surveyResult.howToRead2022.section1.axis2-title'),
          this.reference.Year === 2020 ? this.$t('surveyResult.processFlow') : this.$t('surveyResult.howToRead2022.section1.axis3-title'),
          this.reference.Year === 2020 ? this.$t('surveyResult.salesProducts') : this.$t('surveyResult.howToRead2022.section1.axis4-title'),
        ],
        datasets: [
          {
            label: this.sectorMaturityIn,
            data: [
              this.reference.Infrastructure.Mean,
              this.reference.HumanResources.Mean,
              this.reference.ProcessFlow.Mean,
              this.reference.SalesProducts.Mean,
            ],
            borderColor: this.otherColor,
            backgroundColor: this.otherColorFaded,
            pointBackgroundColor: this.otherColor,
            pointRadius: 5,
          },
          (this.surveySession) ? ({
            label: (this.compareSurveySession) ? `${this.companyName} ${this.surveyDateString}` : this.$t('surveyResult.yourMaturityIn'),
            data: [
              this.surveySession.Infrastructure.Value,
              this.surveySession.HumanResources.Value,
              this.surveySession.ProcessFlow.Value,
              this.surveySession.SalesProducts.Value,
            ],
            borderColor: this.myColor,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: this.myColor,
            pointRadius: 5,
          }) : {},
        ],
      };

      if (this.compareSurveySession) {
        this.sectorComparisonData.datasets.push({
          label: `${this.comparisonCompanyName} ${this.comparisonSurveyDateString}`,
          data: [
            this.compareSurveySession.Infrastructure.Value,
            this.compareSurveySession.HumanResources.Value,
            this.compareSurveySession.ProcessFlow.Value,
            this.compareSurveySession.SalesProducts.Value,
          ],
          borderColor: this.compareColor,
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointBackgroundColor: this.compareColor,
          pointRadius: 5,
        });
      }

      // Global Distribution
      const labels = [];
      const indexes = [];
      for (let i = 0; i <= 9; i += 1) {
        if (i < 9) {
          labels.push(this.$t('surveyResult.fromTo', { n1: i * 10, n2: (i + 1) * 10 - 1 }));
          indexes.push(`${i * 10}-${(i + 1) * 10 - 1}`);
        } else {
          labels.push(this.$t('surveyResult.fromTo', { n1: 90, n2: 100 }));
          indexes.push('90-100');
        }

        if (this.getMyScore() / 10 >= i && this.getMyScore() / 10 < i + 1) {
          this.myPositionIndex = i;
        }
        if (this.compareSurveySession && this.getComparisonScore() / 10 >= i && this.getComparisonScore() / 10 < i + 1) {
          this.comparisonPositionIndex = i;
        }
      }
      this.globalDistributionData = {
        labels,
        datasets: [
          {
            data: indexes.map((index) => this.reference.Score[index]),
            backgroundColor: this.otherColor,
          },
        ],
      };
    },
  },
  computed: {
    sectorMaturityIn() {
      switch (this.code) {
        case 'S':
          return this.$t('surveyResult.sectorMaturityInS', { S: String(this.$t(`surveyFilter.S.${this.category}`)).toLowerCase() });
        case 'T':
          return this.$t('surveyResult.sectorMaturityInT', { T: String(this.$t(`surveyFilter.T.${this.category.replace('-', '_').replace('+', '')}`)).toLowerCase() });
        case 'P':
          return this.$t('surveyResult.sectorMaturityInP', { P: String(this.$t(`surveyFilter.PDisplay.${this.category}`)) });
        case 'M':
          return this.$t('surveyResult.sectorMaturityInM', { M: String(this.$t(`surveyFilter.MDisplay.${this.category}`)) });
        case 'Z':
          return this.$t('surveyResult.sectorMaturityInZ', { Z: String(this.$t(`surveyFilter.ZDisplay.${this.category}`)) });
        case 'E':
          return this.$t('surveyResult.sectorMaturityInE', { E: String(this.$t(`surveyFilter.EDisplay.${this.category}`)) });
        case 'ECO':
          return this.$t('surveyResult.sectorMaturityInECO', { ECO: String(this.$t(`surveyFilter.ECODisplay.${this.category}`)) });
        case 'A':
          return this.$t('surveyResult.sectorMaturityInA', { A: String(this.$t(`surveyFilter.ADisplay.${this.category}`)) });
        default:
          return this.$t('surveyResult.sectorMaturityIn');
      }
    },
    compareSectorTitle() {
      switch (this.code) {
        case 'S':
          return this.$t('surveyResult.compareSectorTitleS', { S: String(this.$t(`surveyFilter.S.${this.category}`)).toLowerCase() });
        case 'T':
          return this.$t('surveyResult.compareSectorTitleT', { T: String(this.$t(`surveyFilter.T.${this.category.replace('-', '_').replace('+', '')}`)).toLowerCase() });
        case 'P':
          return this.$t('surveyResult.compareSectorTitleP', { P: String(this.$t(`surveyFilter.PDisplay.${this.category}`)) });
        case 'M':
          return this.$t('surveyResult.compareSectorTitleM', { M: String(this.$t(`surveyFilter.MDisplay.${this.category}`)) });
        case 'Z':
          return this.$t('surveyResult.compareSectorTitleZ', { Z: String(this.$t(`surveyFilter.ZDisplay.${this.category}`)) });
        case 'E':
          return this.$t('surveyResult.compareSectorTitleE', { E: String(this.$t(`surveyFilter.EDisplay.${this.category}`)) });
        case 'ECO':
          return this.$t('surveyResult.compareSectorTitleECO', { ECO: String(this.$t(`surveyFilter.ECODisplay.${this.category}`)) });
        case 'A':
          return this.$t('surveyResult.compareSectorTitleA', { A: String(this.$t(`surveyFilter.ADisplay.${this.category}`)) });
        default:
          return this.$t('surveyResult.compareSectorTitle');
      }
    },
    referenceCount() {
      switch (this.code) {
        case 'S':
          return this.$t('surveyResult.referenceCountInS', { S: String(this.$t(`surveyFilter.S.${this.category}`)).toLowerCase() });
        case 'T':
          return this.$t('surveyResult.referenceCountInT', { T: String(this.$t(`surveyFilter.T.${this.category.replace('-', '_').replace('+', '')}`)).toLowerCase() });
        case 'P':
          return this.$t('surveyResult.referenceCountInP', { P: String(this.$t(`surveyFilter.PDisplay.${this.category}`)) });
        case 'M':
          return this.$t('surveyResult.referenceCountInM', { M: String(this.$t(`surveyFilter.MDisplay.${this.category}`)) });
        case 'Z':
          return this.$t('surveyResult.referenceCountInZ', { Z: String(this.$t(`surveyFilter.ZDisplay.${this.category}`)) });
        case 'E':
          return this.$t('surveyResult.referenceCountInE', { E: String(this.$t(`surveyFilter.EDisplay.${this.category}`)) });
        case 'ECO':
          return this.$t('surveyResult.referenceCountInECO', { ECO: String(this.$t(`surveyFilter.ECODisplay.${this.category}`)) });
        case 'A':
          return this.$t('surveyResult.referenceCountInA', { A: String(this.$t(`surveyFilter.ADisplay.${this.category}`)) });
        default:
          return this.$t('surveyResult.referenceCountInGLOB');
      }
    },
  },
};
</script>

<style scoped>
.dataset-table-container table {
  width: 100%;
  border-top: 1px solid #eee;
  margin-top: 1em;
  border-collapse: separate;
  border-spacing: 5px;
}
h2 small {
  font-size: 14px;
}
</style>
